import { useState } from "react";
import { Text } from "@/components/ui/text";
import type { ChatMemo } from "./types";
import CloseIcon from "../ui/icons/CloseIcon";

interface ChatMemoFormProps {
  chatTitle: string;
  onBack: () => void;
  onDelete: () => void;
  onEdit: (memo: Partial<ChatMemo>) => void;
}

const ChatMemoForm = ({
  chatTitle,
  onBack,
  onDelete,
  onEdit,
}: ChatMemoFormProps) => {
  const [editedTitle, setEditedTitle] = useState("");
  const [editedContent, setEditedContent] = useState("");

  const handleSave = () => {
    if (!editedTitle.trim() && !editedContent.trim()) {
      // 제목과 내용이 모두 비어있으면 저장하지 않음
      return;
    }
    onEdit({
      title: editedTitle || "Untitled",
      content: editedContent,
    });
  };

  return (
    <div className="flex h-full flex-col gap-6 bg-white">
      {/* Header */}
      <div className="flex items-end justify-between px-6">
        <Text variant="title-md">{chatTitle}</Text>
        <button
          onClick={onBack}
          className="flex h-10 w-10 items-center justify-center"
        >
          <CloseIcon color="#333333" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6">
        <div className="flex h-full flex-col gap-4">
          <input
            type="text"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            placeholder="Title"
            className="focus:outline-none w-full h-[30px] text-[18px] font-pretendard font-normal leading-[26px] tracking-[0.15px] placeholder:text-[18px] placeholder:font-pretendard placeholder:font-normal placeholder:leading-[26px] placeholder:tracking-[0.15px]"
            autoFocus
          />
          <textarea
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            placeholder="Type something."
            className="focus:outline-none h-full min-h-[100px] w-full resize-none text-[18px] font-pretendard font-normal leading-[26px] tracking-[0.15px] placeholder:text-[18px] placeholder:font-pretendard placeholder:font-normal placeholder:leading-[26px] placeholder:tracking-[0.15px]"
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
        <button
          onClick={onDelete}
          className="flex h-10 w-10 items-center justify-center bg-grey-95 rounded-sm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="18"
            viewBox="0 0 16 18"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.31244 0C5.28083 0 4.44455 0.836282 4.44455 1.86789V2.62161C4.44455 2.63673 4.44491 2.65177 4.44562 2.66672H0.888889C0.397969 2.66672 0 3.06469 0 3.55561V5.33339C0 5.82431 0.39797 6.22228 0.888889 6.22228H1.36621L2.09087 15.6428C2.18354 16.8476 3.18811 17.7778 4.39639 17.7778H11.6036C12.8119 17.7778 13.8165 16.8476 13.9091 15.6428L14.6338 6.22228H15.1111C15.602 6.22228 16 5.82431 16 5.33339V3.55561C16 3.06469 15.602 2.66672 15.1111 2.66672H11.5546C11.5553 2.65177 11.5557 2.63673 11.5557 2.62161V1.86789C11.5557 0.836282 10.7194 0 9.68777 0H6.31244Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.68777 0.888889H6.31244C5.77175 0.888889 5.33344 1.3272 5.33344 1.86789V2.62161C5.33344 2.64649 5.35361 2.66667 5.3785 2.66667H10.6217C10.6466 2.66667 10.6668 2.64649 10.6668 2.62161V1.86789C10.6668 1.3272 10.2285 0.888889 9.68777 0.888889ZM6.31244 0C5.28083 0 4.44455 0.836282 4.44455 1.86789V2.62161C4.44455 2.63673 4.44491 2.65177 4.44562 2.66672H0.888889C0.397969 2.66672 0 3.06469 0 3.55561V5.33339C0 5.82431 0.39797 6.22228 0.888889 6.22228H1.36621L2.09087 15.6428C2.18354 16.8476 3.18811 17.7778 4.39639 17.7778H11.6036C12.8119 17.7778 13.8165 16.8476 13.9091 15.6428L14.6338 6.22228H15.1111C15.602 6.22228 16 5.82431 16 5.33339V3.55561C16 3.06469 15.602 2.66672 15.1111 2.66672H11.5546C11.5553 2.65177 11.5557 2.63673 11.5557 2.62161V1.86789C11.5557 0.836282 10.7194 0 9.68777 0H6.31244ZM13.7423 6.22228H2.25772L2.97714 15.5747C3.03419 16.3163 3.65259 16.8889 4.39639 16.8889H11.6036C12.3474 16.8889 12.9658 16.3163 13.0229 15.5747L13.7423 6.22228ZM14.7022 5.33339H15.1111V3.55561H0.888889V5.33339H1.29783H1.77778H14.2222H14.7022ZM6.39022 13.6546C6.14633 13.6824 5.9261 13.5072 5.89832 13.2633L5.40216 8.90816C5.37438 8.66428 5.54956 8.44405 5.79344 8.41627C6.03732 8.38849 6.25755 8.56367 6.28534 8.80755L6.7815 13.1627C6.80928 13.4066 6.6341 13.6268 6.39022 13.6546ZM9.48708 13.6546C9.73097 13.6824 9.95119 13.5072 9.97898 13.2633L10.4751 8.90816C10.5029 8.66428 10.3277 8.44405 10.0839 8.41627C9.83997 8.38849 9.61975 8.56367 9.59196 8.80755L9.0958 13.1627C9.06802 13.4066 9.2432 13.6268 9.48708 13.6546Z"
              fill="black"
            />
          </svg>
        </button>

        <button
          onClick={handleSave}
          className="rounded-lg bg-primary-40 px-[48px] py-[8px] text-white transition-colors hover:bg-primary-50"
        >
          <Text variant="title-lg" className="text-white">
            Save
          </Text>
        </button>
      </div>
    </div>
  );
};

export default ChatMemoForm;
